"use client"

import React, { useState, useEffect, useRef, useMemo } from "react"
import { useTranslation } from "react-i18next"
import { Info, Calendar as CalendarIcon, Plus, Minus, Trash2, List, Phone, MessageCircle } from "lucide-react"
import PhoneInput from 'react-phone-number-input'
import 'react-phone-number-input/style.css'
import { Input } from "@/components/ui/input"
import { Textarea } from "@/components/ui/textarea"
import { Checkbox } from "@/components/ui/checkbox"
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Calendar as CalendarComponent } from "@/components/ui/calendar"
import { DateTime } from "luxon"
import { ru } from "date-fns/locale"
import { useStaffScheduleByDate } from "@/hooks/calendar/useStaffScheduleByDate"
import { useAllStaff } from "@/hooks/calendar/useAllStaff"

import type { Service } from "@/lib/types/appointment"
import { useAppointmentModal } from "@/contexts/AppointmentModalContext"
import { useCompanySettingsInfo } from "@/hooks/shared/useCompanySettingsInfo"
import { salonPhoneCountry } from "@/lib/phone/salonCountry"
// Centralized cost calculation engine
import { createServiceFromCatalog, applyQuantityChange, repriceServicesForMaster } from "@/lib/appointment/costEngine"
import { addSecondsToTime, formatTimeFromString, cn, computeDurationMinutes, formatDurationMinutes, formatCurrency } from "@/lib/utils"
import { getServiceRegistry } from "@/lib/services/serviceRegistrySingleton";
import { Guard } from "@/components/access/Guard"
import { useAvailableServices } from "@/hooks/modal/useAvailableServices"
import { useQueryClient } from "@tanstack/react-query"
import { fetchAvailableServices } from "@/lib/services/appointmentModalService"
import { RecordAttachments } from "@/components/custom/attachments/RecordAttachments"
import { BusyOverlapWarning } from "./busy-overlap-warning"

function formatDate(dateStr?: string | null) {
  if (!dateStr) return "—";
  const d = DateTime.fromISO(dateStr);
  if (!d.isValid) return "—";
  // Format as DD.MM
  return `${d.day.toString().padStart(2, "0")}.${d.month.toString().padStart(2, "0")}`;
}

// computeDuration is now provided by the shared computeDurationMinutes utility in @/lib/utils
// Returns { hours, minutes } from the shared totalMin value (null-safe).
function computeDuration(startTime?: string, endTime?: string) {
  const totalMin = computeDurationMinutes(startTime, endTime);
  if (totalMin === null) return { hours: 0, minutes: 0 };
  return { hours: Math.floor(totalMin / 60), minutes: totalMin % 60 };
}

const timeToMinutes = (time: string) => {
  const [h, m] = time.split(":").map(Number);
  if (!Number.isFinite(h) || !Number.isFinite(m)) return NaN;
  return h * 60 + m;
};

// Helper: add duration to startTime in HH:mm format, return HH:mm formatted string
function addDurationToStart(startTime: string, hours: number, minutes: number, dateStr: string) {
  try {
    const [startHours, startMinutes] = startTime.split(":").map(Number);
    let totalMinutes = startHours * 60 + startMinutes + hours * 60 + minutes;
    totalMinutes = ((totalMinutes % (24 * 60)) + (24 * 60)) % (24 * 60); // wrap around 24h
    const h = Math.floor(totalMinutes / 60);
    const m = totalMinutes % 60;
    return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
  } catch {
    return startTime;
  }
}

// Resolve effective duration (seconds) for a service using ServiceRegistry with master overrides
const resolveServiceDurationSeconds = (
  service: Service,
  masterId?: number,
  lengthLookup?: Map<number, number>
): number => {
  const registry = getServiceRegistry?.();
  const idNum = Number(service.id);
  let duration = 0;

  if (registry && Number.isFinite(idNum)) {
    const descriptor =
      registry.getServiceById?.(idNum) ||
      registry.getSnapshot?.()?.services?.[idNum];

    if (descriptor) {
      const rawCustom =
        masterId && descriptor.staffDurationSeconds
          ? descriptor.staffDurationSeconds[masterId]
          : undefined;
      const custom = Number(rawCustom);
      if (Number.isFinite(custom) && custom > 0) {
        duration = custom;
      } else {
        const def = Number(descriptor.duration_seconds);
        if (Number.isFinite(def) && def > 0) {
          duration = def;
        }
      }
    }
  }

  if (!duration && idNum && lengthLookup?.has(idNum)) {
    duration = lengthLookup.get(idNum) ?? 0;
  }

  if (!duration && typeof service.length === "number" && service.length > 0) {
    duration = service.length;
  }

  return duration > 0 ? duration : 0;
};

const buildDurationDetails = (totalLengthSec: number, startTime: string, t: (key: string, fallback: string) => string) => {
  const newEndTime = addSecondsToTime(startTime, totalLengthSec);
  const hours = Math.floor(totalLengthSec / 3600);
  const minutes = Math.floor((totalLengthSec % 3600) / 60);
  const totalMin = hours * 60 + minutes;
  const durationStr = (formatDurationMinutes(totalMin) || t("appointment.details.zero_minutes", "0 min.")) + ` (${startTime} – ${newEndTime})`;

  return {
    endTime: newEndTime,
    detailsSection: {
      startTimeString: startTime,
      endTimeString: newEndTime,
      serviceDurationString: durationStr,
      durationHours: hours,
      durationMinutes: minutes,
    },
  };
};

export function DetailsSection() {
  const { t } = useTranslation()
  const { data: companySettings } = useCompanySettingsInfo()
  const defaultPhoneCountry = salonPhoneCountry(companySettings?.country_iso2, companySettings?.phone_code)
  const { appointment, updateAppointment } = useAppointmentModal();
  const queryClient = useQueryClient();

  // Fetch staff list for selector
  const { data: allStaff } = useAllStaff();

  // Local staff selection to drive availability before committing to appointment
  const [selectedStaffId, setSelectedStaffId] = useState<number | undefined>(() => {
    if (appointment?.specialistId) return Number(appointment.specialistId);
    if (appointment?.staff?.id) return Number(appointment.staff.id);
    return undefined;
  });

  // Keep local selection in sync when appointment changes externally
  useEffect(() => {
    if (appointment?.specialistId) {
      setSelectedStaffId(Number(appointment.specialistId));
    } else if (appointment?.staff?.id) {
      setSelectedStaffId(Number(appointment.staff.id));
    } else {
      setSelectedStaffId(undefined);
    }
  }, [appointment?.specialistId, appointment?.staff?.id]);

  // Filter staff to the same specialization as the current appointment's master
  const filteredStaff = useMemo(() => {
    if (!allStaff) return [];
    const currentSpecialization = appointment?.staff?.specialization;
    if (!currentSpecialization) return allStaff;
    return allStaff.filter((staff: any) => {
      const staffSpec = String(staff.specialization || "").toLowerCase();
      const currentSpecLower = String(currentSpecialization || "").toLowerCase();
      return (
        staffSpec === currentSpecLower ||
        Number(staff.id) === Number(selectedStaffId)
      );
    });
  }, [allStaff, appointment?.staff?.specialization, selectedStaffId]);

  // Duration state is always in sync with appointment times
  const [duration, setDuration] = useState(() => {
    const computed = computeDuration(appointment?.startTime, appointment?.endTime);
    return {
      hours: isNaN(computed.hours) ? 0 : computed.hours,
      minutes: isNaN(computed.minutes) ? 0 : computed.minutes
    };
  });
  // Sync duration state with appointment times
  useEffect(() => {
    const computed = computeDuration(appointment?.startTime, appointment?.endTime);
    setDuration({
      hours: isNaN(computed.hours) ? 0 : computed.hours,
      minutes: isNaN(computed.minutes) ? 0 : computed.minutes
    });
  }, [appointment?.startTime, appointment?.endTime]);

  // State for popups must be at the top level to maintain hook order
  const [calendarOpen, setCalendarOpen] = useState(false);
  const [startOpen, setStartOpen] = useState(false);
  const [serviceDialogOpen, setServiceDialogOpen] = useState(false);
  const [serviceSearch, setServiceSearch] = useState("");
  const [endOpen, setEndOpen] = useState(false);

  // Local state for comment with debounced update
  const [comment, setComment] = useState(appointment?.comment || "");
  const commentTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Local state for phone with debounced update (full E.164 format, e.g. "+375291234567")
  const [phone, setPhone] = useState(appointment?.client?.phone || "");
  const phoneTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Local state for email with debounced update  
  const [email, setEmail] = useState(appointment?.client?.email || "");
  const emailTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Local state for name with debounced update
  const [name, setName] = useState(appointment?.client?.name || "");
  const nameTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Sync comment state with appointment
  useEffect(() => {
    setComment(appointment?.comment || "");
  }, [appointment?.comment]);

  // Sync phone state with appointment
  useEffect(() => {
    setPhone(appointment?.client?.phone || "");
  }, [appointment?.client?.phone]);

  // Sync email state with appointment
  useEffect(() => {
    setEmail(appointment?.client?.email || "");
  }, [appointment?.client?.email]);

  // Sync name state with appointment
  useEffect(() => {
    setName(appointment?.client?.name || "");
  }, [appointment?.client?.name]);

  // Debounced comment handler
  const handleCommentChange = (value: string) => {
    setComment(value);
    if (commentTimeoutRef.current) clearTimeout(commentTimeoutRef.current);
    commentTimeoutRef.current = setTimeout(() => {
      updateAppointment({ comment: value });
    }, 500);
  };

  // Debounced phone handler (receives full E.164 value from PhoneInput)
  const handlePhoneChange = (value: string) => {
    setPhone(value);
    if (phoneTimeoutRef.current) clearTimeout(phoneTimeoutRef.current);
    phoneTimeoutRef.current = setTimeout(() => {
      updateAppointment({ client: { phone: value } as any });
    }, 500);
  };

  // Debounced email handler
  const handleEmailChange = (value: string) => {
    setEmail(value);
    if (emailTimeoutRef.current) clearTimeout(emailTimeoutRef.current);
    emailTimeoutRef.current = setTimeout(() => {
      updateAppointment({ client: { email: value } as any });
    }, 500);
  };

  // Debounced name handler
  const handleNameChange = (value: string) => {
    setName(value);
    if (nameTimeoutRef.current) clearTimeout(nameTimeoutRef.current);
    nameTimeoutRef.current = setTimeout(() => {
      updateAppointment({ client: { name: value } as any });
    }, 500);
  };
  const startInputRef = useRef<HTMLInputElement>(null);
  const endInputRef = useRef<HTMLInputElement>(null);
  const calendarRef = useRef<HTMLDivElement>(null);
  const startRef = useRef<HTMLDivElement>(null);
  const endRef = useRef<HTMLDivElement>(null);
  const { data: schedule, isLoading: loadingSchedule, error: scheduleQueryError } = useStaffScheduleByDate();

  // Set error message if query fails
  const scheduleError = scheduleQueryError ? t("appointment.details.schedule_error", "Schedule loading error") : null;

  // Compute available dates for the selected master as a Set of YYYY-MM-DD
  const masterId = selectedStaffId;
  const availableDates = React.useMemo(() => {
    if (!masterId || !schedule) return null;
    return new Set(
      Object.entries(schedule)
        .filter(([date, masterIds]) => Array.isArray(masterIds) && masterIds.includes(masterId))
        .map(([date]) => date)
    );
  }, [masterId, schedule]);

  // Compute disabled days dynamically based on the displayed month (covers current + next month for 2-month view)
  const selectedDate = appointment?.date ? DateTime.fromISO(appointment.date) : undefined;
  const displayMonth = selectedDate || DateTime.now();
  const disabledDays = React.useMemo(() => {
    if (!masterId || !availableDates) return undefined;
    // Cover current month and next month for 2-month calendar view
    const start = displayMonth.startOf("month");
    const end = displayMonth.plus({ days: 62 }).endOf("month");
    const days: Date[] = [];
    let d = start;
    while (d <= end) {
      if (!availableDates.has(d.toISODate())) {
        days.push(d.toJSDate());
      }
      d = d.plus({ days: 1 });
    }
    return days;
  }, [masterId, availableDates, displayMonth.year, displayMonth.month]);

  const formattedDate = appointment?.date || undefined;
  const formattedStartTime = formatTimeFromString(appointment?.startTime) || "";
  const formattedEndTime = formatTimeFromString(appointment?.endTime) || "";

  // Fetch available services (needed for duration fallback) — safe even if masterId is undefined
  const { data: availableServices } = useAvailableServices(masterId);
  const lengthByServiceId = useMemo(() => {
    if (!Array.isArray(availableServices)) return new Map<number, number>();
    return new Map<number, number>(
      availableServices
        .map((s) => [Number(s.id), typeof s.length === "number" ? s.length : undefined] as const)
        .filter(([, len]) => Number.isFinite(len)) as [number, number][]
    );
  }, [availableServices]);

  // Update appointment object with parsed values if they differ (only for date, not times)
  useEffect(() => {
    if (appointment && formattedDate && appointment.date !== formattedDate) {
      updateAppointment({
        date: formattedDate
      });
    }
  }, [appointment, formattedDate, updateAppointment]);

  // Handle date select
  function handleDateSelect(date: Date | undefined) {
    if (!date) return;
    // Convert native Date to Luxon DateTime
    const dt = DateTime.fromJSDate(date);
    const isoDate = dt.toISODate();
    console.log("Updating appointment date to:", isoDate);
    updateAppointment({ date: isoDate });
    setCalendarOpen(false);
    setTimeout(() => {
      startInputRef.current?.focus();
      setStartOpen(true);
    }, 100);
  }

  // Calendar classNames (match sidebar) — react-day-picker v9 keys (v8→v9
  // map in ui/calendar.tsx). Month captions stay visible (two months side by
  // side), nav arrows stay hidden — the schedule drives month navigation.
  const calendarClassNames = {
    months: "flex flex-col sm:flex-row space-y-4 sm:space-x-4 sm:space-y-0",
    month: "space-y-4",
    month_caption: "flex justify-center pt-1 items-center",
    caption_label: "text-sm font-medium capitalize",
    nav: "hidden",
    button_previous: "hidden",
    button_next: "hidden",
    month_grid: "w-full border-collapse space-y-1",
    weekdays: "flex",
    weekday: "text-muted-foreground rounded-md w-7 font-normal text-[0.7rem]",
    week: "flex w-full mt-2",
    day: "h-7 w-7 text-center text-sm relative p-0 focus-within:relative focus-within:z-20",
    day_button: "h-7 w-7 p-0 font-normal hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground",
    range_middle: "aria-selected:bg-accent aria-selected:text-accent-foreground",
    hidden: "invisible",
  };

  // Generate time options from 09:00 to 21:00 in 5-min steps
  const times: string[] = [];
  for (let h = 9; h <= 21; ++h) {
    for (let m = 0; m < 60; m += 5) {
      if (h === 21 && m > 0) break;
      times.push(`${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`);
    }
  }
  const startMinutesForEnd = appointment?.startTime ? timeToMinutes(appointment.startTime) : NaN;
  const endTimes = Number.isFinite(startMinutesForEnd)
    ? times.filter((t) => timeToMinutes(t) > startMinutesForEnd)
    : times;

  // Close start time picker on outside click
  useEffect(() => {
    function handle(e: MouseEvent) {
      if (startRef.current && !startRef.current.contains(e.target as Node)) setStartOpen(false);
    }
    if (startOpen) document.addEventListener("mousedown", handle);
    return () => document.removeEventListener("mousedown", handle);
  }, [startOpen]);

  // Close start time picker on Escape
  useEffect(() => {
    function handle(e: KeyboardEvent) {
      if (e.key === "Escape") setStartOpen(false);
    }
    if (startOpen) document.addEventListener("keydown", handle);
    return () => document.removeEventListener("keydown", handle);
  }, [startOpen]);

  // Close end time picker on outside click
  useEffect(() => {
    function handle(e: MouseEvent) {
      if (endRef.current && !endRef.current.contains(e.target as Node)) setEndOpen(false);
    }
    if (endOpen) document.addEventListener("mousedown", handle);
    return () => document.removeEventListener("mousedown", handle);
  }, [endOpen]);

  // Close end time picker on Escape
  useEffect(() => {
    function handle(e: KeyboardEvent) {
      if (e.key === "Escape") setEndOpen(false);
    }
    if (endOpen) document.addEventListener("keydown", handle);
    return () => document.removeEventListener("keydown", handle);
  }, [endOpen]);

  // Close calendar on outside click
  useEffect(() => {
    function handle(e: MouseEvent) {
      if (calendarRef.current && !calendarRef.current.contains(e.target as Node)) {
        setCalendarOpen(false);
      }
    }
    if (calendarOpen) document.addEventListener("mousedown", handle);
    return () => document.removeEventListener("mousedown", handle);
  }, [calendarOpen]);

  if (!appointment) {
    return (
      <div className="flex-1 overflow-y-auto p-6 flex items-center justify-center text-gray-500">
        {t("appointment.details.no_data", "No appointment data.")}
      </div>
    );
  }
  const services: Service[] = Array.isArray(appointment.services)
    ? (appointment.services as Service[])
    : [];

  /**
   * Пересчитывает endTime на основе суммарной длины услуг.
   * В DetailsSection всегда синхроним длительность, если есть startTime.
   */
  const recalcEndTimeFromServices = (servicesAfterChange: Service[]) => {
    if (!appointment?.startTime) {
      updateAppointment({ services: servicesAfterChange });
      return;
    }

    const masterId = selectedStaffId;

    const totalLengthSec = servicesAfterChange.reduce((sum, s) => {
      const amount = typeof s.amount === "number" && !Number.isNaN(s.amount) ? s.amount : 1;
      const duration = resolveServiceDurationSeconds(s, masterId, lengthByServiceId);
      return sum + duration * amount;
    }, 0);

    if (!Number.isFinite(totalLengthSec) || totalLengthSec <= 0) {
      updateAppointment({ services: servicesAfterChange });
      return;
    }

    const durationPatch = buildDurationDetails(totalLengthSec, appointment.startTime, t);
    updateAppointment({ services: servicesAfterChange, ...durationPatch });
  };

  const handleQuantityChange = (id: number | string, change: number) => {
    if (!services.length) return;
    const updated = services.map((service) => {
      if (service.id === id) {
        // Use centralized applyQuantityChange to properly recalculate cost fields
        const newQuantity = Math.max(1, (service.amount || 1) + change);
        const patch = applyQuantityChange(service as any, newQuantity);
        return { ...service, ...patch };
      }
      return service;
    });
    recalcEndTimeFromServices(updated);
  };

  const handleRemoveService = (id: number | string) => {
    // An appointment must keep at least one service — saving an empty list
    // strips every yrecordservice row and orphans the record. The backend
    // rejects it too; this stops the admin reaching a save that can only fail.
    // Removing the appointment itself is Delete, not an empty service list.
    if (services.length <= 1) return;
    const updated = services.filter((service) => service.id !== id);
    recalcEndTimeFromServices(updated);
  };

  // Handler to add a service to the appointment - use centralized createServiceFromCatalog
  const handleAddService = (serviceId: number) => {
    if (!availableServices || !appointment) return;
    const selected = availableServices.find((s: any) => s.id === serviceId);
    if (!selected) return;

    let newServices: Service[] = [];
    const existingService = services.find((s) => s.id === serviceId);

    if (existingService) {
      // Increment quantity using centralized applyQuantityChange
      const patch = applyQuantityChange(existingService as any, (existingService.amount || 1) + 1);
      newServices = services.map((s) =>
        s.id === serviceId ? { ...s, ...patch } : s
      );
    } else {
      // Create new service using centralized createServiceFromCatalog
      const newService = createServiceFromCatalog(selected);
      newServices = [...services, newService as Service];
    }

    recalcEndTimeFromServices(newServices);
    setServiceSearch("");
    setServiceDialogOpen(false);
  };

  const success = appointment?.clientStats?.success_visits_count ?? 0;
  const fail = appointment?.clientStats?.fail_visits_count ?? 0;
  const total = appointment?.clientStats?.total_visits_count ?? (success + fail);

  return (
    <div className="flex-1 overflow-y-auto p-6">
      <div className="grid gap-8 grid-cols-1 lg:grid-cols-[1.6fr_0.8fr]">
        {/* Left column */}
        <div>
          <h2 className="text-base font-medium mb-4">{t("appointment.details.client", "Client")}</h2>

          <div className="mb-4">
            <label className="block text-xs mb-1">{t("appointment.details.name", "Name")}</label>
            <div className="relative">
              <Input
                value={name}
                onChange={(e) => handleNameChange(e.target.value)}
                placeholder={t("appointment.details.name_placeholder", "Enter client name")}
                className="pr-8 border-gray-300 rounded h-9 text-base md:text-sm touch-manipulation select-text"
              />
            </div>
          </div>

          <div className="mb-4">
            <div className="flex items-center mb-1">
              <label className="block text-xs">{t("appointment.details.phone", "Phone")}</label>
              <Info className="w-3.5 h-3.5 ml-1 text-gray-400" />
              <span className="mx-2 text-gray-300">|</span>
              <label className="block text-xs">{t("appointment.details.confirmation", "Confirmation")}</label>
              {(appointment?.client?.confirm_method ?? 1) === 1 ? (
                <Phone className="w-3.5 h-3.5 ml-1 text-gray-500" />
              ) : (
                <MessageCircle className="w-3.5 h-3.5 ml-1 text-blue-500" />
              )}
            </div>
            <Guard
              check={(p) => p.timetable.phonesVisible}
              mode="render"
              fallback={<div className="text-xs text-gray-500">{t("appointment.details.hidden_by_access", "Hidden by access settings")}</div>}
            >
              <PhoneInput
                international
                defaultCountry={defaultPhoneCountry}
                countryCallingCodeEditable={false}
                countryOptionsOrder={[
                  'BY', 'RU', 'UA', 'PL', 'LT', 'LV', 'DE', 'US', 'GB', 'IL',
                  '|',
                  '...',
                ]}
                value={phone}
                onChange={(value) => {
                  const newValue = value || '';
                  handlePhoneChange(newValue);
                }}
                className={cn(
                  "phone-input-no-zoom flex h-9 w-full rounded-md border border-input bg-background text-sm ring-offset-background",
                  "[&_.PhoneInputInput]:flex-1 [&_.PhoneInputInput]:bg-transparent [&_.PhoneInputInput]:outline-none [&_.PhoneInputInput]:px-2",
                  "[&_.PhoneInputCountry]:pl-3 [&_.PhoneInputCountry]:pr-1",
                )}
              />
            </Guard>
          </div>

          <div className="mb-4">
            <label className="block text-xs mb-1">Email</label>
            <Input
              value={email}
              onChange={(e) => handleEmailChange(e.target.value)}
              placeholder={t("appointment.details.email_placeholder", "Enter client email")}
              className="border-gray-300 rounded h-9 text-base md:text-sm touch-manipulation select-text"
            />
          </div>

          <div className="mb-4">
            <label className="block text-xs mb-1">{t("appointment.details.loyalty_search", "Loyalty search")}</label>
            <Input
              placeholder={t("appointment.details.loyalty_placeholder", "Enter card, subscription or certificate number")}
              className="border-gray-300 rounded h-9 text-sm"
              readOnly
            />
          </div>

          <div className="flex items-center mb-6">
            <Checkbox id="register-other" className="border-gray-400 rounded w-3.5 h-3.5" disabled />
            <label htmlFor="register-other" className="ml-2 text-xs">
              {t("appointment.details.registers_other", "Registers another visitor")}
            </label>
            <Info className="w-3.5 h-3.5 ml-1 text-gray-400" />
          </div>

          <div className="mb-4">
            <label className="block text-xs mb-1">{t("appointment.details.staff", "Staff")}</label>
            <Select
              value={selectedStaffId != null ? String(selectedStaffId) : undefined}
              onValueChange={(val) => {
                const newId = Number(val);
                setSelectedStaffId(newId);
                setCalendarOpen(true);
                // Clear current date/time so user re-selects for the chosen staff member
                updateAppointment({
                  date: undefined,
                  startTime: undefined,
                  endTime: undefined,
                  specialistId: newId,
                });
                // Per-master prices: lines still at a default price move to the new
                // master's price; a price the admin typed stays (repriceServicesForMaster).
                const linesBefore = services;
                void queryClient
                  .fetchQuery({
                    queryKey: ["availableServices", newId],
                    queryFn: () => fetchAvailableServices(newId),
                    staleTime: 5 * 60 * 1000,
                  })
                  .then((catalog) => {
                    const repriced = repriceServicesForMaster(linesBefore, catalog);
                    if (repriced.some((line, i) => line !== linesBefore[i])) {
                      updateAppointment({ services: repriced });
                    }
                  })
                  .catch(() => {
                    /* the master's list did not load — keep the current prices */
                  });
              }}
            >
              <SelectTrigger className="w-full h-9 border-gray-300 text-sm">
                <SelectValue placeholder={t("appointment.details.select_staff", "Select staff")} />
              </SelectTrigger>
              <SelectContent>
                {filteredStaff.map((staff: any) => (
                  <SelectItem key={staff.id} value={String(staff.id)}>
                    <span>{staff.name}</span>
                    {staff.specialization && (
                      <span className="ml-2 text-xs text-gray-400">
                        ({staff.specialization})
                      </span>
                    )}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="mb-4">
            <label className="block text-xs mb-1">{t("appointment.details.date_time", "Date and time")}</label>
            <div className="grid grid-cols-3 gap-2">
              {/* Calendar popup for date selection */}
              <div className="calendar-container relative">
                <Input
                  type="text"
                  value={appointment.date ? formatDate(appointment.date) : ""}
                  className="pr-8 border-gray-300 rounded h-9 text-sm cursor-pointer"
                  readOnly
                  onClick={() => setCalendarOpen(true)}
                  tabIndex={0}
                />
                <span
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"
                >
                  <CalendarIcon className="w-3.5 h-3.5" />
                </span>
                {/* Calendar popup — opens UPWARD (bottom-full) so it isn't
                    clipped at the bottom of the modal on short viewports. */}
                {calendarOpen && (
                  <div
                    ref={calendarRef}
                    className="absolute z-50 bg-white border rounded-lg shadow-lg bottom-full mb-2 left-0"
                    style={{ minWidth: 320 }}
                    tabIndex={-1}
                    onBlur={e => {
                      // Close if focus leaves popup
                      if (!e.currentTarget.contains(e.relatedTarget as Node)) setCalendarOpen(false);
                    }}
                  >
                    <div className="p-2">
                      {loadingSchedule && (
                        <div className="text-xs text-gray-500 py-4">{t("appointment.details.loading_schedule", "Loading schedule...")}</div>
                      )}
                      {scheduleError && (
                        <div className="text-xs text-red-500 py-4">{scheduleError}</div>
                      )}
                      {!loadingSchedule && !scheduleError && (
                        <CalendarComponent
                          mode="single"
                          numberOfMonths={2}
                          selected={selectedDate ? selectedDate.toJSDate() : undefined}
                          onSelect={handleDateSelect}
                          locale={ru}
                          defaultMonth={selectedDate ? selectedDate.toJSDate() : new Date()}
                          showOutsideDays={true}
                          fixedWeeks
                          disabled={disabledDays}
                          className="w-full px-4 pt-2 pb-3"
                          classNames={calendarClassNames}
                          modifiersClassNames={{
                            selected: "[&>button]:bg-amber-500 [&>button]:text-white [&>button]:hover:bg-amber-500 [&>button]:hover:text-white [&>button]:focus:bg-amber-500 [&>button]:focus:text-white [&>button]:rounded-full [&>button]:shadow-sm",
                            today: "[&>button]:bg-orange-500 [&>button]:text-white [&>button]:rounded-full",
                            disabled: "text-muted-foreground opacity-50",
                            outside: "text-muted-foreground opacity-50",
                          }}
                        />
                      )}
                    </div>
                    <div className="flex justify-center p-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setCalendarOpen(false)}
                        type="button"
                      >
                        {t("appointment.details.close", "Close")}
                      </Button>
                    </div>
                  </div>
                )}
              </div>
              <div className="relative" ref={startRef}>
                <Input
                  type="text"
                  value={formattedStartTime}
                  className="pr-8 border-gray-300 rounded h-9 text-sm cursor-pointer"
                  ref={startInputRef}
                  readOnly
                  onClick={() => setStartOpen((v) => !v)}
                  tabIndex={0}
                />
                <span className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none">
                  <CalendarIcon className="w-3.5 h-3.5" />
                </span>
                {startOpen && (
                  <div className="absolute z-50 bg-white border rounded shadow-lg mt-2 left-0 w-full max-h-60 overflow-y-auto overscroll-contain touch-pan-y">
                    {times.map((t) => (
                      <button
                        key={t}
                        className={`w-full text-left px-3 py-2 text-sm hover:bg-amber-100 ${formattedStartTime === t ? "bg-amber-200 font-bold" : ""
                          }`}
                        onClick={() => {
                          setStartOpen(false);
                          // When changing startTime, also update endTime based on current duration
                          if (appointment?.date) {
                            const newStart = t;
                            const newEnd = addDurationToStart(newStart, duration.hours, duration.minutes, appointment.date);
                            const staffObj = allStaff?.find((s) => Number(s.id) === selectedStaffId);
                            updateAppointment({
                              specialistId: selectedStaffId,
                              // Roster staff (lib/types/staff.ts, id: number) and appointment
                              // staff (lib/types/appointment.ts, id: string) are different
                              // shapes — map the fields across instead of spreading, which
                              // would replace the string id with a number.
                              staff: staffObj
                                ? {
                                    ...appointment.staff,
                                    id: String(staffObj.id),
                                    name: staffObj.name,
                                    specialization: staffObj.specialization,
                                    avatar: staffObj.avatar,
                                  }
                                : appointment.staff,
                              date: appointment.date,
                              startTime: newStart,
                              endTime: newEnd,
                            });

                            // After start time selection, move focus to end time and open dropdown
                            setTimeout(() => {
                              endInputRef.current?.focus();
                              setEndOpen(true);
                            }, 100);
                          }
                        }}
                        type="button"
                      >
                        {t}
                      </button>
                    ))}
                  </div>
                )}
              </div>
              <div className="relative" ref={endRef}>
                <Input
                  type="text"
                  value={formattedEndTime}
                  className="pr-8 border-gray-300 rounded h-9 text-sm cursor-pointer"
                  readOnly
                  onClick={() => setEndOpen((v) => !v)}
                  tabIndex={0}
                  ref={endInputRef}
                />
                <span className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none">
                  <CalendarIcon className="w-3.5 h-3.5" />
                </span>
                {endOpen && (
                  <div className="absolute z-50 bg-white border rounded shadow-lg mt-2 left-0 w-full max-h-60 overflow-y-auto overscroll-contain touch-pan-y">
                    {endTimes.map((t) => (
                      <button
                        key={t}
                        className={`w-full text-left px-3 py-2 text-sm hover:bg-amber-100 ${formattedEndTime === t ? "bg-amber-200 font-bold" : ""
                          }`}
                        onClick={() => {
                          setEndOpen(false);
                          if (appointment?.date) {
                            updateAppointment({ endTime: t });
                          }
                        }}
                        type="button"
                      >
                        {t}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
            {/* the master's own calendar or Booksy holds this time: a warning, never a refusal */}
            <BusyOverlapWarning
              staffId={selectedStaffId}
              date={appointment.date}
              startTime={formattedStartTime}
              endTime={formattedEndTime}
            />
          </div>

          {/* Duration section */}
          <div className="mb-4">
            <label className="block text-xs mb-1">{t("appointment.details.duration", "Duration")}</label>
            <div className="grid grid-cols-2 gap-2">
              <div className="relative">
                <select
                  className="w-full h-9 px-3 border border-gray-300 rounded-md appearance-none text-sm"
                  value={duration.hours}
                  onChange={(e) => {
                    const newHours = Number(e.target.value);
                    setDuration((prev) => ({ ...prev, hours: newHours }));
                    if (appointment && appointment.startTime && appointment.date) {
                      const newEndTime = addDurationToStart(appointment.startTime, newHours, duration.minutes, appointment.date);
                      updateAppointment({ endTime: newEndTime });
                    }
                  }}
                  disabled={!appointment.startTime}
                >
                  {Array.from({ length: 13 }, (_, i) => (
                    <option key={i} value={i}>
                      {i} {t("appointment.details.hour_abbr", "hr")}
                    </option>
                  ))}
                </select>
                <div className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
                  <svg className="h-3.5 w-3.5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </div>
              </div>
              <div className="relative">
                <select
                  className="w-full h-9 px-3 border border-gray-300 rounded-md appearance-none text-sm"
                  value={duration.minutes}
                  onChange={(e) => {
                    const newMinutes = Number(e.target.value);
                    setDuration((prev) => ({ ...prev, minutes: newMinutes }));
                    if (appointment && appointment.startTime && appointment.date) {
                      const newEndTime = addDurationToStart(appointment.startTime, duration.hours, newMinutes, appointment.date);
                      updateAppointment({ endTime: newEndTime });
                    }
                  }}
                  disabled={!appointment.startTime}
                >
                  {Array.from({ length: 12 }, (_, i) => (
                    <option key={i * 5} value={i * 5}>
                      {i * 5} {t("appointment.details.min_abbr", "min")}
                    </option>
                  ))}
                </select>
                <div className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
                  <svg className="h-3.5 w-3.5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </div>
              </div>
            </div>
            {!appointment.startTime && (
              <div className="text-xs text-gray-400 mt-1">{t("appointment.details.select_start_first", "Select start time first")}</div>
            )}
          </div>

          {/* Услуги — верстка из StatusSection, но только колонки: Услуга, Кол-во, удаление */}
          <div className="mt-4">
            <h3 className="text-sm font-medium mb-2">{t("appointment.details.services_in_record", "Services in record")}</h3>

            <div className="border rounded-lg p-3 bg-white">
              {/* Заголовок колонок */}
              <div className="grid grid-cols-12 gap-2 text-[11px] font-medium text-gray-500 mb-2">
                <div className="col-span-8">{t("appointment.details.service_or_product", "Service or product")}</div>
                <div className="col-span-3 text-center">{t("appointment.details.quantity", "Qty")}</div>
                <div className="col-span-1"></div>
              </div>

              {services.map((service) => {
                const amount = Number.isFinite(service.amount) ? (service.amount as number) : 1;

                return (
                  <div
                    key={service.id ?? service.title}
                    className="grid grid-cols-12 gap-2 mb-3 items-center"
                  >
                    {/* Услуга / мастер */}
                    <div className="col-span-8">
                      <div className="flex items-start gap-2">
                        <div className="mt-0.5">
                          <div className="w-6 h-6 rounded-full bg-gray-100 flex items-center justify-center">
                            <span className="text-[10px]">⚙️</span>
                          </div>
                        </div>
                        <div className="min-w-0">
                          <div className="text-xs font-medium truncate">
                            {service.title || t("appointment.details.untitled", "Untitled")}
                          </div>
                          {appointment.staff?.name && (
                            <div className="text-[11px] text-gray-500 truncate">
                              {appointment.staff.name}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Кол-во (кнопки как в статусе) */}
                    <div className="col-span-3 flex justify-center items-center">
                      <button
                        type="button"
                        className="w-7 h-7 rounded-full border border-gray-300 flex items-center justify-center text-gray-600"
                        onClick={() => handleQuantityChange(service.id!, -1)}
                        disabled={amount <= 1}
                      >
                        <Minus className="w-3 h-3" />
                      </button>
                      <span className="mx-2 text-xs w-5 text-center">{amount}</span>
                      <button
                        type="button"
                        className="w-7 h-7 rounded-full border border-gray-300 flex items-center justify-center text-gray-600"
                        onClick={() => handleQuantityChange(service.id!, +1)}
                      >
                        <Plus className="w-3 h-3" />
                      </button>
                    </div>

                    {/* Удаление */}
                    <div className="col-span-1 flex justify-end">
                      <button
                        type="button"
                        className="w-7 h-7 rounded-full border border-gray-200 flex items-center justify-center text-gray-400 hover:text-red-500 hover:border-red-200 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:text-gray-400 disabled:hover:border-gray-200"
                        onClick={() => handleRemoveService(service.id!)}
                        disabled={services.length <= 1}
                        title={
                          services.length <= 1
                            ? t("appointment.modal.last_service_required", "An appointment must keep at least one service")
                            : undefined
                        }
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>
                );
              })}

              {/* Сообщение, если услуг нет */}
              {services.length === 0 && (
                <div className="p-3 text-center text-xs text-gray-500 bg-gray-50 rounded">
                  {t("appointment.details.no_services", "No services in record")}
                </div>
              )}
            </div>

            {/* Add service button */}
            <div className="mt-3">
              <Button
                variant="outline"
                className="border border-gray-300 rounded-md px-3 h-8 text-xs flex items-center"
                onClick={() => {
                  setServiceSearch("");
                  setServiceDialogOpen(true);
                }}
                type="button"
              >
                <List className="w-3.5 h-3.5 mr-1.5" />
                {t("appointment.details.add_service", "Add service")}
              </Button>

              {/* Service List Modal */}
              <Dialog open={serviceDialogOpen} onOpenChange={setServiceDialogOpen}>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>{t("appointment.details.select_service", "Select service")}</DialogTitle>
                    <DialogDescription>
                      {t("appointment.details.select_service_desc", "List of available services to add to the record. Select one.")}
                    </DialogDescription>
                  </DialogHeader>
                  {!availableServices && (
                    <div className="text-xs text-gray-500 py-4">{t("appointment.details.loading_services", "Loading services...")}</div>
                  )}
                  {availableServices && (
                    <div className="flex flex-col gap-3 w-full min-w-0">
                      <Input
                        placeholder={t("appointment.details.search_service", "Search service...")}
                        value={serviceSearch}
                        onChange={(e) => setServiceSearch(e.target.value)}
                        className="text-sm"
                      />
                      <div className="max-h-64 overflow-y-auto overscroll-contain touch-pan-y">
                        {(() => {
                          const filtered = Array.isArray(availableServices)
                            ? availableServices.filter((s: any) =>
                              s.title.toLowerCase().includes(serviceSearch.toLowerCase())
                            )
                            : [];

                          if (filtered.length > 0) {
                            return (
                              <ul>
                                {filtered.map((service: any) => (
                                  <li key={service.id}>
                                    <Button
                                      variant="ghost"
                                      className="w-full justify-start text-xs overflow-hidden"
                                      onClick={() => handleAddService(service.id)}
                                    >
                                      <span className="truncate">{service.title}</span>
                                    </Button>
                                  </li>
                                ))}
                              </ul>
                            );
                          }

                          return (
                            <div className="text-xs text-gray-500 py-4">{t("appointment.details.nothing_found", "Nothing found")}</div>
                          );
                        })()}
                      </div>
                    </div>
                  )}
                </DialogContent>
              </Dialog>
            </div>
          </div>

          <div className="mb-4">
            <label className="block text-xs mb-1">{t("appointment.details.comment", "Comment")}</label>
            {/* the comment's photos and videos (boona docs/codebase/attachments/): attach, drop or paste */}
            {appointment?.id ? (
              <RecordAttachments recordId={Number(appointment.id)}>
                <Textarea
                  className="border-gray-300 min-h-[100px] text-base md:text-sm touch-manipulation select-text"
                  placeholder={t("appointment.details.comment_placeholder", "Add a comment to the record")}
                  value={comment}
                  onChange={(e) => handleCommentChange(e.target.value)}
                />
              </RecordAttachments>
            ) : (
              <Textarea
                className="border-gray-300 min-h-[100px] text-base md:text-sm touch-manipulation select-text"
                placeholder={t("appointment.details.comment_placeholder", "Add a comment to the record")}
                value={comment}
                onChange={(e) => handleCommentChange(e.target.value)}
              />
            )}
          </div>
        </div>

        {/* Right column */}
        <div>
          <h2 className="text-base font-medium mb-3">{t("appointment.details.client_info", "Client information")}</h2>
          <div className="space-y-2 mb-4">
            <div className="flex justify-between text-xs">
              <span className="text-gray-600">{t("appointment.details.visits", "Visits:")} </span>
              <span>{Number.isFinite(total) ? total : "—"}</span>
            </div>
            <div className="flex justify-between text-xs">
              <span className="text-gray-600">{t("appointment.details.misses", "Misses:")} </span>
              <span>{Number.isFinite(fail) ? fail : "—"}</span>
            </div>
          </div>

          <div className="flex mb-6">
            {appointment.detailsSection?.clientTags && appointment.detailsSection.clientTags.length > 0 ? (
              appointment.detailsSection.clientTags.map((tag: any) => (
                <span
                  key={tag.id}
                  className="px-2 py-0.5 text-[10px] font-medium rounded mr-2"
                  style={{
                    backgroundColor: tag.color || "#eee",
                    color: tag.font_color || "#222",
                  }}
                >
                  {tag.title}
                </span>
              ))
            ) : null}
          </div>

          <h2 className="text-base font-medium mb-3">{t("appointment.details.record_details", "Record details")}</h2>

          <div className="space-y-2 mb-4">
            <div>
              <div className="text-xs text-gray-600">
                {t("appointment.details.created_at", "Created:")}{" "}<span className="font-medium">{appointment.detailsSection?.createdAt ?? "—"}</span>
              </div>
            </div>
            <div>
              <div className="text-xs text-gray-600">
                {t("appointment.details.created_by", "Создан:")}{" "}<span className="font-medium">{appointment.detailsSection?.createdUser ?? "—"}</span>
              </div>
            </div>
            <div>
              <div className="text-xs text-gray-600">
                {t("appointment.details.visit_status", "Visit status:")}{" "}<span className="font-medium">{appointment.detailsSection?.visitStatus ?? "—"}</span>
              </div>
            </div>

            {appointment.detailsSection?.comment && appointment.detailsSection.comment !== "—" && (
              <div>
                <div className="text-xs text-gray-600">
                  {t("appointment.details.comment", "Comment:")}: <span className="font-medium">{appointment.detailsSection.comment}</span>
                </div>
              </div>
            )}
            <div>
              <div className="text-xs text-gray-600">
                {t("appointment.details.confirmation_label", "Confirmation:")}{" "}
                <span className="font-medium">
                  {(appointment?.client?.confirm_method ?? 1) === 1
                    ? t("appointment.details.confirm_phone", "phone")
                    : appointment?.client?.messenger_label
                      // the salon's own name for a channel it answers by hand (c1 → "Instagram"), MAX, VK
                      ? appointment.client.messenger_label
                    : appointment?.client?.messenger === "telegram"
                      ? t("appointment.details.confirm_telegram", "Telegram")
                      : appointment?.client?.messenger === "whatsapp"
                        ? t("appointment.details.confirm_whatsapp", "WhatsApp")
                        : appointment?.client?.messenger === "viber"
                          ? t("appointment.details.confirm_viber", "Viber")
                          : t("appointment.details.confirm_messenger", "messenger")}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
